/*!
 * Eveline custom analytics
 * Emits Web Pixels custom events via Shopify.analytics.publish.
 * Loaded with `defer` from layout/theme.liquid.
 *
 * Strategy:
 *   - One transparent fetch wrapper for /cart/* and /search/suggest endpoints.
 *   - Delegated click listeners on document (bubble + capture for wishlist).
 *   - Delegated change listener on document.
 *   - Listener for theme:drawer:open dispatched by theme.js on window.
 *   - Lazy cart cache (filled on first interaction, refreshed on cart mutations).
 *   - Zero work on page load beyond listener registration.
 */
(function () {
  'use strict';

  if (window.__evelineAnalyticsReady) return;
  window.__evelineAnalyticsReady = true;

  // ─── Helpers (ported 1:1 from web pixel) ──────────────────────────────
  function getColorValue(input) {
    if (!input) return '';
    var m = String(input).match(/^color:([^|]*)/);
    return m ? m[1] : '';
  }
  function getCategories(input) {
    if (!input) return '';
    var v = String(input).split('|').slice(1);
    return v.length ? v.join(',') : '';
  }
  function getFirstCategory(input) {
    if (!input) return '';
    var v = String(input).split('|').slice(1);
    return v.length ? v[0] : '';
  }
  function getProductType(type, variantTitle) {
    if (type && /Zestaw|Set/i.test(type)) return 'bundle';
    var hasVariant = variantTitle && variantTitle !== 'Default Title' && variantTitle !== 'Default';
    return hasVariant ? 'configurable' : 'simple';
  }

  // Shopify cart / line endpoints return prices in cents (integer)
  function fromCents(cents) {
    if (cents == null) return '0.00';
    return (Number(cents) / 100).toFixed(2);
  }

  function getCurrency() {
    return (
      (window.eveline && window.eveline.shop && window.eveline.shop.currency) ||
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      ''
    );
  }

  // ─── Debug mode ────────────────────────────────────────────────────────
  // Enable in browser:  localStorage.setItem('eveline:analytics:debug', '1')
  // Or via URL param:   ?evdebug=1   (sticky for the session)
  // Disable:            localStorage.removeItem('eveline:analytics:debug')
  var DEBUG = false;
  try {
    if (/[?&]evdebug=1\b/.test(window.location.search)) {
      try { sessionStorage.setItem('eveline:analytics:debug', '1'); } catch (e) {}
    }
    DEBUG =
      (window.localStorage && window.localStorage.getItem('eveline:analytics:debug') === '1') ||
      (window.sessionStorage && window.sessionStorage.getItem('eveline:analytics:debug') === '1');
  } catch (e) {}

  if (DEBUG && console && console.info) {
    console.info('%c[eveline-analytics] debug ON', 'color:#e62688;font-weight:bold');
  }

  // ─── track() — single emission point ───────────────────────────────────
  var _queue = [];
  function track(eventName, data) {
    if (DEBUG && console && console.log) {
      // %c styling so the event line is easy to spot in DevTools
      console.log('%c[ev:track]%c ' + eventName, 'color:#e62688;font-weight:bold', 'color:inherit', data || {});
    }
    try {
      var api = window.Shopify && window.Shopify.analytics;
      if (api && typeof api.publish === 'function') {
        api.publish(eventName, data || {});
      } else {
        _queue.push([eventName, data || {}]);
        if (DEBUG && console && console.warn) {
          console.warn('[eveline-analytics] queued (Shopify.analytics not ready):', eventName);
        }
      }
    } catch (e) {
      if (DEBUG && console && console.error) console.error('[eveline-analytics] publish failed:', e);
    }
  }
  // Drain queue once Web Pixels API becomes available (bounded retry)
  (function drainPoll(n) {
    var api = window.Shopify && window.Shopify.analytics;
    if (api && typeof api.publish === 'function') {
      while (_queue.length) {
        var ev = _queue.shift();
        try { api.publish(ev[0], ev[1]); } catch (e) {}
      }
      return;
    }
    if (n > 60) return; // ~30s max
    setTimeout(function () { drainPoll(n + 1); }, 500);
  })(0);

  // Expose for debugging / ad-hoc emission
  window.eveline = window.eveline || {};
  window.eveline.analytics = {
    track: track,
    debug: function (on) {
      try {
        if (on === false) {
          window.localStorage.removeItem('eveline:analytics:debug');
          window.sessionStorage.removeItem('eveline:analytics:debug');
          console.info('[eveline-analytics] debug OFF (reload to apply)');
        } else {
          window.localStorage.setItem('eveline:analytics:debug', '1');
          console.info('[eveline-analytics] debug ON (reload to apply)');
        }
      } catch (e) {}
    }
  };

  // ─── Item builders ─────────────────────────────────────────────────────
  function itemFromCartLine(line, qtyOverride) {
    var product = line.product || {};
    var productType = line.product_type || product.type || '';
    var variantTitle = (line.variant_title || (line.variant && line.variant.title) || '').toString();
    var sku = line.sku || (line.variant && line.variant.sku) || '';
    var vendor = line.vendor || product.vendor || '';
    var title = line.product_title || product.title || line.title || '';
    var priceCents = (line.final_price != null) ? line.final_price : (line.price != null ? line.price : 0);
    var price = fromCents(priceCents);
    var qty = qtyOverride != null ? qtyOverride : (line.quantity || 1);

    return {
      item_id: sku,
      item_name: title,
      item_variant: variantTitle,
      item_brand: vendor,
      item_lowest_price: price,
      price: price,
      quantity: qty,
      item_type: getProductType(productType, variantTitle),
      item_category: getFirstCategory(productType),
      item_category2: getColorValue(productType),
      item_categories: getCategories(productType)
    };
  }

  // Read currently selected PDP variant (for view-style events)
  function itemsFromVariantJSON(scope) {
    var json = scope
      ? scope.querySelector('script[id^="variant-json-"]')
      : document.querySelector('script[id^="variant-json-"]');
    if (!json) return [];
    try {
      var v = JSON.parse(json.textContent);
      var price = (typeof v.price === 'number') ? fromCents(v.price) : String(v.price || '');
      var productType = v.product_type || '';
      return [{
        item_id: v.sku || '',
        item_name: v.name || v.product_title || (document.title || ''),
        item_variant: v.public_title || v.title || '',
        item_brand: v.vendor || '',
        item_lowest_price: price,
        price: price,
        quantity: 1,
        item_type: getProductType(productType, v.public_title || v.title || ''),
        item_category: getFirstCategory(productType),
        item_category2: getColorValue(productType),
        item_categories: getCategories(productType)
      }];
    } catch (e) {
      return [];
    }
  }

  // From theme's variantChanged custom event.
  // Theme dispatches from two sources with different shapes:
  //   1. <product-options-element> (radio/select) — variant from variant-json,
  //      has v.public_title / v.title (e.g. "100ml / Red").
  //   2. <eveline-color-switcher> — variant from window.product_variants,
  //      has only v.option_1/2/3 (no title field, no parent product object).
  // We reconstruct variant title and pull product metadata from window.eveline.product.
  function variantToItem(v) {
    if (!v) return null;
    var price = (typeof v.price === 'number') ? fromCents(v.price) : String(v.price || '');

    // Variant title — try every shape
    var variantTitle = v.public_title || v.title || '';
    if (!variantTitle) {
      var parts = [];
      // Two naming conventions: option_1 (eveline) and option1 (Shopify standard)
      if (v.option_1 || v.option1) parts.push(v.option_1 || v.option1);
      if (v.option_2 || v.option2) parts.push(v.option_2 || v.option2);
      if (v.option_3 || v.option3) parts.push(v.option_3 || v.option3);
      variantTitle = parts.join(' / ');
    }

    // Product context — prefer parent product on variant, fallback to window.eveline.product
    var product = v.product || {};
    var evProduct = (window.eveline && window.eveline.product) || {};
    var productType = product.type || evProduct.type || '';
    var productTitle = product.title || evProduct.title || '';
    var vendor = product.vendor || evProduct.vendor || '';

    if (!productTitle) {
      var h1 = document.querySelector('main h1, .product-title, [data-item="section-heading"]');
      if (h1) productTitle = (h1.textContent || '').trim();
    }

    return {
      item_id: v.sku || '',
      item_name: productTitle,
      item_variant: variantTitle,
      item_brand: vendor,
      item_lowest_price: price,
      price: price,
      quantity: 1,
      item_type: getProductType(productType, variantTitle),
      item_category: getFirstCategory(productType),
      item_category2: getColorValue(productType),
      item_categories: getCategories(productType)
    };
  }

  function itemsFromWishlistBtn(btn) {
    // MiB Wishlist exposes only handle + variant id on the button.
    // SKU/title/price not available without enrichment — emit minimal item.
    return [{
      item_id: btn.getAttribute('data-mib-product-handle') || '',
      item_name: '',
      item_brand: '',
      item_lowest_price: '',
      price: '',
      quantity: 1,
      product_handle: btn.getAttribute('data-mib-product-handle') || '',
      variant_id: btn.getAttribute('data-mib-variant-id') || ''
    }];
  }

  // ─── Cart cache ────────────────────────────────────────────────────────
  // Prefilled from Liquid in theme.liquid (window.eveline.cart) so go_to_cart
  // and remove_from_cart work on the very first user click — no HTTP needed
  // until cart actually mutates.
  var cartCache = (window.eveline && window.eveline.cart) || null;
  var cartFetchInflight = null;
  function fetchCart() {
    if (cartFetchInflight) return cartFetchInflight;
    cartFetchInflight = fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (c) { cartCache = c; cartFetchInflight = null; return c; })
      .catch(function () { cartFetchInflight = null; });
    return cartFetchInflight;
  }
  // Lazy fill — only on first interaction needing cart context
  var cartFillTriggered = false;
  function ensureCartCache() {
    if (cartCache || cartFillTriggered) return;
    cartFillTriggered = true;
    fetchCart();
  }

  // ─── Fetch wrapper (transparent) ───────────────────────────────────────
  if (window.fetch && !window.__evelineFetchWrapped) {
    window.__evelineFetchWrapped = true;
    var _fetch = window.fetch.bind(window);

    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';

      var isAdd = /\/cart\/add(\.js)?(\?|$)/.test(url);
      var isChange = /\/cart\/change(\.js)?(\?|$)/.test(url);
      var isUpdate = /\/cart\/update(\.js)?(\?|$)/.test(url);
      var isClear = /\/cart\/clear(\.js)?(\?|$)/.test(url);
      var isSuggest = /\/search\/suggest(\.json)?(\?|$)/.test(url);

      // Predictive search — read query from URL params
      if (isSuggest) {
        try {
          var u = new URL(url, window.location.origin);
          var q = u.searchParams.get('q');
          if (q) onSearchSuggest(q);
        } catch (e) {}
      }

      // Snapshot pre-state for change/update (full removal detection)
      var preCart = (isChange || isUpdate || isClear) ? cartCache : null;

      var promise = _fetch(input, init);

      if (isAdd || isChange || isUpdate || isClear) {
        promise.then(function (res) {
          if (!res || !res.ok) return;
          // Clone so we don't consume the original response body
          res.clone().json().then(function (json) {
            try {
              if (isAdd) onCartAdd(json);
              else if (isChange || isUpdate) onCartMutation(json, preCart);
              else if (isClear) onCartClear(preCart);
            } catch (e) {}
          }).catch(function () {});
        }).catch(function () {});
      }

      return promise;
    };
  }

  // ─── Cart event handlers ───────────────────────────────────────────────
  function onCartAdd(response) {
    // /cart/add.js returns the added line OR { items: [...] } when adding multiple via items[]
    var lines = [];
    if (response && Array.isArray(response.items)) {
      lines = response.items;
    } else if (response && (response.product_id || response.variant_id || response.id)) {
      lines = [response];
    }
    if (!lines.length) return;

    var items = lines.map(function (l) { return itemFromCartLine(l, l.quantity); });
    var value = items.reduce(function (s, i) {
      return s + (parseFloat(i.price || 0) * (i.quantity || 1));
    }, 0).toFixed(2);

    track('add_to_cart_custom', {
      currency: getCurrency(),
      value: value,
      items: items
    });

    // Refresh cart cache in background (add response is partial)
    fetchCart();
  }

  function onCartMutation(newCart, preCart) {
    if (!newCart || !Array.isArray(newCart.items)) return;
    cartCache = newCart;
    if (!preCart || !Array.isArray(preCart.items)) return;

    // Detect fully removed lines (key disappeared)
    var byKey = {};
    newCart.items.forEach(function (it) { byKey[it.key] = it; });

    var removed = [];
    preCart.items.forEach(function (prev) {
      if (!byKey[prev.key]) removed.push(prev);
    });

    if (!removed.length) return;
    var items = removed.map(function (r) { return itemFromCartLine(r, r.quantity); });
    track('remove_from_cart', {
      currency: getCurrency(),
      items: items
    });
  }

  function onCartClear(preCart) {
    var items = (preCart && preCart.items || []).map(function (l) {
      return itemFromCartLine(l, l.quantity);
    });
    if (items.length) {
      track('remove_from_cart', {
        currency: getCurrency(),
        items: items
      });
    }
    cartCache = { items: [], item_count: 0, total_price: 0 };
  }

  // ─── Predictive search (debounced, dedupe) ─────────────────────────────
  var searchTimer = null;
  var lastSearchQ = '';
  function onSearchSuggest(query) {
    if (query === lastSearchQ) return;
    clearTimeout(searchTimer);
    searchTimer = setTimeout(function () {
      lastSearchQ = query;
      track('site_search_start', { query: query });
    }, 250);
  }

  // ─── Boot ──────────────────────────────────────────────────────────────
  function boot() {
    // Pre-fill cart cache on cart page (so remove_from_cart can detect lines)
    if (document.body && document.body.dataset && document.body.dataset.tpl === 'cart') {
      fetchCart();
    }

    // ─── Click delegation — CAPTURE phase ───────────────────────────────
    // Runs top-down, before downstream handlers (especially theme.js's
    // [data-drawer-open] handler which calls stopPropagation, eating bubbles
    // before they reach document). Also used for events that need pre-toggle
    // state (collapsible-row aria-expanded, MiB wishlist active flag).
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;

      // go_to_cart — every click on header cart icon (drawer + page mode)
      var cartLink = t.closest('.header--cart');
      if (cartLink) {
        ensureCartCache();
        var c = cartCache;
        track('go_to_cart', {
          sort_type: undefined,
          subtotal: c ? fromCents(c.total_price) : undefined,
          value: c ? fromCents(c.total_price) : undefined,
          cart_quantity: c ? c.item_count : undefined,
          item_count: c ? c.items.length : undefined,
          has_coupons: c ? !!(c.cart_level_discount_applications && c.cart_level_discount_applications.length) : false
        });
      }

      // go_to_checkout — cart form submit may stopPropagation, so capture
      if (t.closest('button[name="checkout"], .cart--checkout-button')) {
        track('go_to_checkout', {});
      }

      // product_details — read aria-expanded BEFORE theme toggles it
      var detailsToggle = t.closest('.collapsible-row--toggle');
      if (detailsToggle && document.body.dataset.tpl === 'product') {
        var wasExpanded = detailsToggle.getAttribute('aria-expanded') === 'true';
        if (!wasExpanded) {
          var labelText = detailsToggle.getAttribute('aria-label') || detailsToggle.textContent || '';
          var detailsType = labelText.trim().toLowerCase().replace(/\s+/g, '_');
          track('product_details', {
            details_type: detailsType,
            currency: getCurrency(),
            items: itemsFromVariantJSON()
          });
        }
      }

      // Wishlist (MiB) — read data-mib-wishlist-active BEFORE MiB toggles it
      var wishBtn = t.closest('.mib-product-wishlist-btn');
      if (wishBtn) {
        var wasActive = String(wishBtn.getAttribute('data-mib-wishlist-active') || '').toLowerCase() === 'true';
        if (wasActive) {
          track('remove_from_wishlist', {
            product_id: wishBtn.getAttribute('data-mib-product-handle') || ''
          });
        } else {
          track('add_to_wishlist', {
            currency: getCurrency(),
            items: itemsFromWishlistBtn(wishBtn)
          });
        }
      }
    }, { capture: true });

    // ─── Click delegation — BUBBLE phase ────────────────────────────────
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;

      // product_review_pagination — counter on the host element
      var reviewMore = t.closest('.eveline-reviews__button--more');
      if (reviewMore) {
        var host = reviewMore.closest('eveline-reviews');
        var page = host ? parseInt(host.dataset.evtReviewsPage || '1', 10) : 1;
        var next = page + 1;
        if (host) host.dataset.evtReviewsPage = String(next);
        track('product_review_pagination', { page_number: next });
      }

      // Targeted opt-in clicked event
      var clickHook = t.closest('[data-evt-click]');
      if (clickHook) {
        track('clicked', {
          element: {
            id: clickHook.id || '',
            value: clickHook.value || '',
            href: clickHook.href || '',
            name: clickHook.getAttribute('name') || '',
            tagName: clickHook.tagName || ''
          }
        });
      }

      // Ensure cart cache is filled on any cart-area interaction
      if (t.closest('.cart--root, .header--cart, [data-drawer-view="cart-drawer"]')) {
        ensureCartCache();
      }
    }, { passive: true });

    // ─── Change delegation ──────────────────────────────────────────────
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;

      // sort_option_select
      if (t.matches && t.matches('input[name="sort_by"]')) {
        track('sort_option_select', {
          sort_type: t.dataset.label || t.value || ''
        });
        return;
      }

      // filter_select — refine inputs only
      if (t.matches && t.matches('input.filter--input') && t.closest('li.filter--link[data-type="refine"]')) {
        track('filter_select', {
          filter_type: t.name || '',
          filter_content: t.dataset.label || t.value || ''
        });
        return;
      }
    }, { passive: true });

    // ─── choose_product_variant — subscribe to theme's variantChanged event
    // theme.js dispatches `theme:product:{data-id}:variantChanged` on window
    // with full variant payload after every variant switch (radio, select,
    // color swatch — all paths). One listener per <product-options-element>;
    // rescan when new ones appear (quick-add drawer, dynamic loads).
    var variantHooked = new WeakSet();
    function hookVariantChanged(el) {
      if (!el || variantHooked.has(el)) return;
      var id = el.getAttribute('data-id');
      if (!id) return;
      variantHooked.add(el);
      window.addEventListener('theme:product:' + id + ':variantChanged', function (ev) {
        var v = ev && ev.detail && ev.detail.variant;
        var item = v ? variantToItem(v) : null;
        track('choose_product_variant', {
          currency: getCurrency(),
          items: item ? [item] : []
        });
      });
    }
    document.querySelectorAll('product-options-element').forEach(hookVariantChanged);
    if (window.MutationObserver) {
      new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          var added = muts[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var n = added[j];
            if (!n || n.nodeType !== 1) continue;
            if (n.matches && n.matches('product-options-element')) hookVariantChanged(n);
            if (n.querySelectorAll) {
              n.querySelectorAll('product-options-element').forEach(hookVariantChanged);
            }
          }
        }
      }).observe(document.body, { childList: true, subtree: true });
    }

    // ─── theme:drawer:open → open_sidebar_cart ──────────────────────────
    // theme.js dispatches via `window.trigger(...)` → event lands on window, not document
    window.addEventListener('theme:drawer:open', function (e) {
      var detail = e.detail || {};
      if (detail.view === 'cart-drawer') {
        // Pre-fill cart cache on drawer open (so remove_from_cart in drawer works)
        ensureCartCache();
        track('open_sidebar_cart', {});
      }
    });

    // ─── view_item_list — wishlist page (event mis-named in pixel) ─────
    if (/\/apps\/mib-wishlist\b|\/wishlist\b/.test(window.location.pathname)) {
      track('view_item_list', {
        currency: getCurrency(),
        items: [],
        item_list_id: '',
        item_list_name: 'Lista życzeń'
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
